import { mountLandingAudio } from '../audio/landing-audio';
import { inline } from './markdown';
import {
  A, cmsGet, el, guideTile, loadCSS, newsTile, renderFooter, siteHeader, unloadCSS, wirePlayLinks,
  type GuideItem, type NewsItem,
} from './site-shell';

function initFaq(root: HTMLElement) {
  const mount = root.querySelector('#faq-list');
  if (!mount) return;
  cmsGet<Array<{ question: string; answer: string }>>('/faq').then(rows => {
    if (!rows?.length) return;
    const frag = document.createDocumentFragment();
    rows.forEach(r => {
      const d = el('details');
      d.appendChild(el('summary', null, r.question));
      const p = el('p');
      p.innerHTML = inline(r.answer);
      d.appendChild(p);
      frag.appendChild(d);
    });
    mount.replaceChildren(frag);
  }).catch(() => {});
}

function initHomeTeasers(root: HTMLElement) {
  const newsMount = root.querySelector('#home-news');
  if (newsMount) {
    cmsGet<{ items: NewsItem[] }>('/news?limit=3').then(data => {
      const items = data.items || [];
      if (!items.length) return;
      const tiles = el('div', 'tiles');
      items.forEach(i => tiles.appendChild(newsTile(i)));
      newsMount.appendChild(tiles);
      newsMount.closest('section')!.hidden = false;
    }).catch(() => {});
  }

  const guideMount = root.querySelector('#home-guides');
  if (guideMount) {
    cmsGet<{ items: GuideItem[] }>('/guides').then(data => {
      const items = (data.items || []).slice(0, 3);
      if (!items.length) return;
      const tiles = el('div', 'tiles');
      items.forEach(i => tiles.appendChild(guideTile(i)));
      guideMount.appendChild(tiles);
      guideMount.closest('section')!.hidden = false;
    }).catch(() => {});
  }
}

function initRails(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>('[data-rail]').forEach(rail => {
    let startX = 0, startLeft = 0, dragging = false;
    rail.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      if (rail.scrollWidth <= rail.clientWidth) return;
      dragging = true; startX = e.clientX; startLeft = rail.scrollLeft;
      rail.classList.add('dragging');
    });
    rail.addEventListener('pointermove', (e: PointerEvent) => {
      if (!dragging) return;
      e.preventDefault();
      rail.scrollLeft = startLeft - (e.clientX - startX);
    });
    function end() {
      if (!dragging) return;
      dragging = false;
      rail.classList.remove('dragging');
    }
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('pointerleave', end);
    rail.addEventListener('click', (e) => {
      if (Math.abs(rail.scrollLeft - startLeft) > 4) { e.preventDefault(); e.stopPropagation(); }
    }, true);
  });
}

function landingBody(): string {
  return `
${siteHeader('home')}

  <main id="main">
    <div class="hero" id="top">
      <div class="hero-bg" aria-hidden="true"></div>
      <div class="wrap">
        <img class="hero-logo px" src="${A}/logo-hero.png" alt="Terraboma" width="900" height="419" />
        <h1>Farm the ash. Craft your gear. <em>Survive the night.</em></h1>
        <div class="cta-row">
          <a class="btn-board btn-board-lg" data-play="" href="#">Play free</a>
        </div>
      </div>
    </div>

    <section class="story" id="world" aria-labelledby="world-h">
      <div class="wrap">
        <p class="eyebrow">The legend of Terraboma</p>
        <h2 id="world-h">The volcano took the valley. One shroom brought it back.</h2>
        <p class="lead">
          Ash smothered the fields and the farmsteads emptied. Then something small and purple
          pushed up through the crust, and green followed it. Your job is to keep it spreading.
        </p>
        <div class="panels rail" data-rail>
          <figure class="panel">
            <img src="${A}/art/story_1_trim.jpg" alt="A lone traveller crosses a cracked, ash-covered valley beneath a smoking volcano." loading="lazy" width="1172" height="769" />
            <figcaption><b>Before</b>Cold ground, dead trees, houses no one comes back to.</figcaption>
          </figure>
          <figure class="panel">
            <img src="${A}/art/story_3_trim.jpg" alt="A small purple mushroom character stands in a strip of new grass and flowers cutting through the ash." loading="lazy" width="1174" height="769" />
            <figcaption><b>The shroom</b>Life comes back one tile at a time — and it follows you.</figcaption>
          </figure>
          <figure class="panel">
            <img src="${A}/art/story_5_trim.jpg" alt="A thriving village with market stalls, wheat fields and vegetable beds under a green mountain." loading="lazy" width="1173" height="769" />
            <figcaption><b>After</b>Fields, a market, neighbours. All of it built by hand.</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section class="band-bg band-loop" aria-labelledby="loop-h">
      <div class="wrap">
        <p class="eyebrow">One world, many ways to play</p>
        <h2 id="loop-h">Everything connects to everything</h2>
        <p class="lead">
          There are no separate menus of mini-games here. Wood you chop becomes the workbench
          that unlocks the axe that gets you more wood, faster — and the sword that keeps you
          alive at 2am, game time.
        </p>
        <div class="grid rail" data-rail>
          <div class="card"><img class="ico px" src="${A}/icons/hoe.png" alt="" aria-hidden="true" width="40" height="40" /><h3>Farm anywhere</h3><p>No fixed grid and no cap. Clear scorched ground, hoe it, and plant on any tile you've earned — as many beds as your energy will carry, spread wherever you like.</p></div>
          <div class="card"><img class="ico px" src="${A}/icons/hand.png" alt="" aria-hidden="true" width="40" height="40" /><h3>Gather everything</h3><p>Trees, ore rocks, loose stone, mushrooms and bushes all drop real items on the ground. Walk over them to pick them up — or lose them.</p></div>
          <div class="card"><img class="ico px" src="${A}/icons/hammer.png" alt="" aria-hidden="true" width="40" height="40" /><h3>Build your workshop</h3><p>Workbench, then furnace, then forge. Each station you raise opens the next branch of the recipe tree — 66 craftable pieces in all.</p></div>
          <div class="card"><img class="ico px" src="${A}/icons/sword.png" alt="" aria-hidden="true" width="40" height="40" /><h3>Fight for your farm</h3><p>Six kinds of monster prowl the dark. Torches and campfires keep them off your crops; a sword or bow handles the ones that get through.</p></div>
          <div class="card"><img class="ico px" src="${A}/icons/energy.png" alt="" aria-hidden="true" width="40" height="40" /><h3>Energy that matters</h3><p>Every swing costs something, and energy refills slowly — about an hour from empty. Rest, eat a cooked meal, or sleep the night out in your tent to skip the wait.</p></div>
          <div class="card"><img class="ico px" src="${A}/icons/trophy.png" alt="" aria-hidden="true" width="40" height="40" /><h3>Goals worth chasing</h3><p>Over sixty achievements across eight categories, daily and weekly quests, and three live leaderboards — most experienced, richest farmer and arena champions.</p></div>
        </div>
      </div>
    </section>

    <section id="farm" aria-labelledby="farm-h" style="background:linear-gradient(180deg,var(--bark-2),var(--bark));border-block:3px solid var(--wood)">
      <div class="wrap split">
        <div>
          <p class="eyebrow">Working the land</p>
          <h2 id="farm-h">28 things to grow, from two-minute wheat to fruit trees</h2>
          <p class="lead">
            Twenty-two field crops, two berry bushes that keep producing, and four fruit trees
            that come back season after season. Cheap seeds pay off in minutes; the expensive
            ones take over an hour and are worth the wait.
          </p>
          <ul class="ticks">
            <li><b>Water your beds</b> and they finish sooner — skip it and you'll wait.</li>
            <li><b>Unlocks by level.</b> Wheat on day one, watermelon and pear trees much later.</li>
            <li><b>Sell at the market</b> where prices drift ±15% every in-game day, the same for everyone.</li>
            <li><b>Cook what you kill.</b> Raw meat is a gamble; 20 seconds on a campfire makes it worth eating.</li>
          </ul>
        </div>
        <div class="art">
          <div class="sprite-tray sprite-tray-crops" role="img" aria-label="All 28 Terraboma crops, from wheat and carrots to watermelon, berries and fruit trees.">
            ${['wheat','carrot','potato','turnip','beet','cabbage','onion','garlic','leek','pea',
               'cucumber','tomato','paprika','pepper','chili','eggplant','corn','pumpkin','watermelon','sunflower',
               'strawberry','grape','red_berry','purple_berry','apple','pear','peach','cherry']
              .map(c => `<div><img class="px" src="${A}/crops/${c}_3.png" alt="" width="48" height="48" loading="lazy" /></div>`).join('\n            ')}
          </div>
        </div>
      </div>
    </section>

    <section class="band-bg band-craft" id="craft" aria-labelledby="craft-h">
      <div class="wrap split flip">
        <div class="art">
          <div class="sprite-tray" role="img" aria-label="Crafting stations and materials.">
            ${['station_workbench','station_furnace','station_forge','ingot_iron','wood','stone','ore','gem']
              .map(s => `<div><img class="px" src="${A}/art/${s}.png" alt="" width="48" height="48" loading="lazy" /></div>`).join('\n            ')}
          </div>
        </div>
        <div>
          <p class="eyebrow">Tools, weapons, armour</p>
          <h2 id="craft-h">A nine-tier ladder, eight slots, sixty-six recipes</h2>
          <p class="lead">
            Axe, pickaxe, hoe, sword, bow, helmet, chest and legs. Tools and weapons start at
            Wood and climb all nine rungs; armour joins the ladder at Stone. Better tools swing harder and gather faster, which is the only way to afford
            the tier above.
          </p>
          <div class="ladder" role="list" aria-label="Gear tiers">
            ${[['1_wood','Wood'],['2_stone','Stone'],['3_copper','Copper'],['4_iron','Iron'],['5_gold','Gold'],['6_mythril','Mythril'],['emerald','Emerald'],['amber','Amber'],['ruby','Ruby']]
              // Tiers 7-9 have no ladder icon of their own; the game's ingot sprite stands in.
              .map(([f,n]) => `<span class="tier" role="listitem"><img class="px" src="${/^\d/.test(f) ? `${A}/art/tier_${f}.png` : `/sprites/items/ingot_${f}.png`}" alt="" aria-hidden="true" width="18" height="18" loading="lazy" />${n}</span>`).join('\n            ')}
          </div>
          <ul class="ticks" style="margin-top:22px">
            <li><b>Gems are the bottleneck.</b> A rock drops one about a quarter of the time above ground.</li>
            <li><b>Dungeons are the fix.</b> Three depths down, gem chance climbs from 15% to 70% — and the skeletons get twice as tough.</li>
            <li><b>Armour shows on your hero.</b> What you craft is what you're wearing.</li>
          </ul>
        </div>
      </div>
    </section>

    <section class="night" id="night" aria-labelledby="night-h">
      <div class="wrap">
        <p class="eyebrow">When the light goes</p>
        <h2 id="night-h">A full day takes twenty real minutes. Four of them are dark.</h2>
        <p class="lead">
          Night falls for everyone at once. Monsters spawn out at the edge of your vision and
          walk in — unless there's a torch, a campfire or your tent nearby. Die and you leave a
          gravestone holding some of your coins and a slice of your resources; it stands beside
          you at your respawn point, and you have ten minutes to pick it back up.
        </p>
        <div class="mob-row rail" data-rail>
          <div class="mob"><img class="px" src="${A}/art/skeleton.png" alt="" aria-hidden="true" loading="lazy" width="48" height="48" /><b>Skeleton</b><span>shoots from six tiles out</span></div>
          <div class="mob"><img class="px" src="${A}/art/orc.png" alt="" aria-hidden="true" loading="lazy" width="48" height="48" /><b>Orc</b><span>closes the gap in a dash</span></div>
          <div class="mob"><img class="px" src="${A}/icons/crown.png" alt="" aria-hidden="true" loading="lazy" width="48" height="48" /><b>Orc Chief</b><span>night five onward, calls for help</span></div>
          <div class="mob"><img class="px" src="${A}/icons/fire.png" alt="" aria-hidden="true" loading="lazy" width="48" height="48" /><b>Volcano things</b><span>cowlings and flying skulls</span></div>
          <div class="mob"><img class="px" src="${A}/icons/shield.png" alt="" aria-hidden="true" loading="lazy" width="48" height="48" /><b>Your defences</b><span>torches, fences, a good sword</span></div>
        </div>
        <div class="grid rail" data-rail style="margin-top:26px">
          <div class="card"><h3>Light is a mechanic</h3><p>Torches cost two wood and a stone. A campfire costs five wood and three stone and also cooks your dinner. Both keep the dark at arm's length.</p></div>
          <div class="card"><h3>Sleep it off</h3><p>Pitch a tent and you can sleep through the night — waking with full energy and a bit of health back. It's the safest choice, and you lose the hours.</p></div>
          <div class="card"><h3>It escalates</h3><p>The longer you survive, the nastier the roster. Chiefs arrive on night five and bring friends when you hurt them.</p></div>
        </div>
      </div>
    </section>

    <section class="arena" id="arena" aria-labelledby="arena-h">
      <div class="wrap split">
        <div>
          <p class="eyebrow">The main event</p>
          <h2 id="arena-h">The Lava Arena — 25 in, one walks out</h2>
          <p class="lead">
            A 120×120 tile island of lava and shroom clouds. A few seconds to read the ground,
            then three and a half minutes of everyone-for-themselves. Kills pay gold, and gold
            only counts once you've carried it back to the safe space in the middle — which
            slams shut for the last minute.
          </p>
          <ul class="ticks">
            <li><b>Ten runs a day, the first three free.</b> Runs four to ten cost 100 coins each.</li>
            <li><b>Want more?</b> Up to ten further entries a day can be bought for 15 Shrooms each — a hard ceiling of twenty runs, so nobody grinds the arena into an economy.</li>
            <li><b>Bank or bust.</b> Die holding unbanked gold and every coin of it is gone.</li>
            <li><b>Streaks compound.</b> Each kill in a row is worth 15% more than the last.</li>
            <li><b>Rated.</b> Every finish moves your Elo, and your placement, kills and banked gold are all shown in the breakdown.</li>
          </ul>
          <div class="cta-row" style="justify-content:flex-start;margin-top:26px">
            <a class="btn-board" data-play="" href="#">Enter the arena</a>
          </div>
        </div>
        <div class="art">
          <div class="stats" style="margin-top:0">
            <div class="stat"><b>25</b><span>fighters</span></div>
            <div class="stat"><b>3.5 min</b><span>battle</span></div>
            <div class="stat"><b>120²</b><span>tiles</span></div>
            <div class="stat"><b>60 s</b><span>safe space shuts</span></div>
          </div>
          <div class="card" style="margin-top:14px;background:rgba(0,0,0,.42);border-color:rgba(255,150,90,.28)">
            <h3>Hazards, not luck</h3>
            <p style="color:#f0c7ae">Lava burns while you stand in it. Shroom clouds slow you by 40%. Geysers telegraph for a second and a half before they blow. Everything is readable — you just have to read it.</p>
          </div>
        </div>
      </div>
    </section>

    <section aria-labelledby="live-h">
      <div class="wrap">
        <p class="eyebrow">Beyond the plot</p>
        <h2 id="live-h">A world that keeps working while you're away</h2>
        <p class="lead">
          Build a house and someone moves in. Tame a horse and the map gets smaller. Everything
          off-screen is worked out from timestamps when you come back — no idle timers running
          your battery down.
        </p>
        <div class="mob-row rail villagers" data-rail>
          <div class="mob"><img class="px" src="${A}/art/villager_farmer.png" alt="" aria-hidden="true" loading="lazy" width="29" height="22" /><b>Farmer</b><span>tends the beds</span></div>
          <div class="mob"><img class="px" src="${A}/art/villager_forester.png" alt="" aria-hidden="true" loading="lazy" width="25" height="20" /><b>Forester</b><span>brings in wood</span></div>
          <div class="mob"><img class="px" src="${A}/art/villager_miner.png" alt="" aria-hidden="true" loading="lazy" width="25" height="20" /><b>Miner</b><span>stone and ore</span></div>
          <div class="mob"><img class="px" src="${A}/art/villager_forager.png" alt="" aria-hidden="true" loading="lazy" width="24" height="22" /><b>Forager</b><span>mushrooms and berries</span></div>
          <div class="mob"><img class="px" src="${A}/art/villager_herder.png" alt="" aria-hidden="true" loading="lazy" width="13" height="28" /><b>Herder</b><span>keeps the animals</span></div>
        </div>
        <div class="grid rail" data-rail>
          <div class="card"><img class="ico px" src="${A}/art/villager_forester.png" alt="" aria-hidden="true" width="40" height="40" loading="lazy" /><h3>Villagers</h3><p>Every house you build makes room for one villager. A forester brings in wood, a miner stone, a forager mushrooms — and they keep at it for up to six hours while you're offline.</p></div>
          <div class="card"><img class="ico px" src="${A}/art/horse.png" alt="" aria-hidden="true" width="40" height="40" loading="lazy" /><h3>Horses</h3><p>Wild horses can be tamed with carrots and hay — 40% odds bare-handed, better with every feed. Work horses carry two extra bag slots, racers are fastest, heavies don't spook after dark.</p></div>
          <div class="card"><img class="ico px" src="${A}/art/cow.png" alt="" aria-hidden="true" width="40" height="40" loading="lazy" /><h3>Animals</h3><p>Chickens, ducks, swans, sheep in three fleece colours, and cows roam the map. Feathers, leather, wool and meat all come from somewhere.</p></div>
          <div class="card"><img class="ico px" src="${A}/art/dungeon_arch.png" alt="" aria-hidden="true" width="40" height="40" loading="lazy" /><h3>Dungeons</h3><p>Three depths per entrance. Richer veins and better gem odds the further down you push, guarded by skeletons that scale with the depth.</p></div>
          <div class="card"><img class="ico px" src="${A}/icons/star.png" alt="" aria-hidden="true" width="40" height="40" loading="lazy" /><h3>The oracle's errand</h3><p>A robed oracle stands by the fountain in the birch grove with one job for you a day: bring the resources she asks for, take the gold.</p></div>
          <div class="card"><img class="ico px" src="${A}/icons/leaf.png" alt="" aria-hidden="true" width="40" height="40" loading="lazy" /><h3>World chat</h3><p>One channel for the whole world, always on, so the valley never feels empty while you're out clearing ash at three in the morning.</p></div>
        </div>
      </div>
    </section>

    <section id="news" aria-labelledby="news-h" hidden
             style="background:linear-gradient(180deg,var(--bark-2),var(--bark));border-block:3px solid var(--wood)">
      <div class="wrap">
        <p class="eyebrow">From the valley</p>
        <h2 id="news-h">Latest news</h2>
        <p class="lead">Patch notes, events and what we're building next.</p>
        <div id="home-news"></div>
        <div class="cta-row" style="justify-content:flex-start;margin-top:24px">
          <a class="btn btn-ghost" href="/news">All news</a>
        </div>
      </div>
    </section>

    <section id="guides" aria-labelledby="guides-h" hidden>
      <div class="wrap">
        <p class="eyebrow">Learn the valley</p>
        <h2 id="guides-h">Guides</h2>
        <p class="lead">How the systems actually work, written by people who play it.</p>
        <div id="home-guides"></div>
        <div class="cta-row" style="justify-content:flex-start;margin-top:24px">
          <a class="btn btn-ghost" href="/guides">All guides</a>
        </div>
      </div>
    </section>

    <section aria-labelledby="cur-h" style="background:linear-gradient(180deg,var(--bark-2),var(--bark));border-block:3px solid var(--wood)">
      <div class="wrap">
        <p class="eyebrow">Two currencies, no confusion</p>
        <h2 id="cur-h">Coins are earned. Shrooms are optional.</h2>
        <p class="lead">
          Every crop, tool, recipe and dungeon is reachable by playing. Shrooms buy convenience — an extra
          rest charge, a head start, a companion, runs past the daily ten — and they convert one way only.
        </p>
        <div class="coins">
          <div class="coin-card">
            <img class="px" src="${A}/icons/coin.png" alt="" aria-hidden="true" width="44" height="44" loading="lazy" />
            <div><h3>Coins</h3><p>Earned by selling produce at the market, clearing quests and achievements, and banking gold in the arena. Coins buy seeds, boosters, villagers and extra arena runs. You start with 100.</p></div>
          </div>
          <div class="coin-card">
            <img class="px" src="${A}/icons/shroom.png" alt="" aria-hidden="true" width="44" height="44" loading="lazy" />
            <div><h3>Shrooms</h3><p>The premium token, bought in packs if you want it. One Shroom exchanges for ten Coins — and never the other way round. Shrooms are never dropped when you die.</p></div>
          </div>
        </div>
      </div>
    </section>

    <section class="how" id="how" aria-labelledby="how-h">
      <div class="wrap">
        <p class="eyebrow">Get going in about a minute</p>
        <h2 id="how-h">How it works</h2>
        <p class="lead">No install, no launcher, no wait.</p>
        <div class="steps rail" data-rail>
          <div class="step"><h3>Make your hero</h3><p>Create a free account and put your character together in the creator — skin, hair, outfit. It takes a minute.</p></div>
          <div class="step"><h3>Clear a patch of ash</h3><p>Chop, hoe, plant, water. Sell the first harvest, buy better seeds, and start hauling wood for a workbench.</p></div>
          <div class="step"><h3>Get through your first night</h3><p>Light a torch, cook something, and decide whether you're fighting or sleeping. Both are valid.</p></div>
          <div class="step"><h3>Take it to the arena</h3><p>Ten runs a day, the first three free — twenty if you spend Shrooms. Bank your gold, outlast twenty-four others, and climb the rating.</p></div>
        </div>
      </div>
    </section>

    <section id="faq" aria-labelledby="faq-h" style="background:linear-gradient(180deg,var(--bark-2),var(--bark));border-block:3px solid var(--wood)">
      <div class="wrap">
        <p class="eyebrow">Before you ask</p>
        <h2 id="faq-h">Frequently asked</h2>
        <div class="faq" id="faq-list">
          <details><summary>Is it really free?</summary><p>Yes. Make an account and play. There's an optional premium token (Shrooms) for players who want to skip a wait, but every crop, tool, recipe and dungeon is reachable by playing. Shrooms add extras: companions, and arena runs past the daily ten.</p></details>
          <details><summary>Do I need to download anything?</summary><p>No. Terraboma runs in any modern browser. It's the same world whether you open it on a laptop or a phone — touch controls, a thumbstick and pinch-zoom are built in.</p></details>
          <details><summary>Is my progress saved?</summary><p>Everything lives on the server, tied to your account. Felled trees, mined rocks, your plots, your gear tiers and your buildings are all remembered between sessions.</p></details>
          <details><summary>What happens if I die?</summary><p>You leave a gravestone holding a share of your coins and part of one resource stack. It appears at your respawn point, right where you wake up, so nothing is stranded; claim it within ten minutes and it's yours again. Premium Shrooms are never at risk.</p></details>
          <details><summary>Do I have to play every day?</summary><p>No. Crops and villagers are worked out from the clock when you return, so nothing is wasted by logging off. Daily quests and the free arena entry reward showing up, but they don't punish you for not.</p></details>
          <details><summary>How many arena runs do I get?</summary><p>Three free entries a day. Runs four to ten cost 100 coins each, so ten a day is the normal ceiling. If you want more, up to ten further entries a day can be bought for 15 Shrooms each — twenty runs in a day is the hard limit for everyone.</p></details>
          <details><summary>Is the arena against real people?</summary><p>Every run is a 25-fighter match. Matches start within seconds — real players who are searching are seated first, and any empty places are filled by AI fighters. Your rating and the leaderboards are shared with everyone.</p></details>
          <details><summary>Can I lose my farm?</summary><p>Never. Monsters can hurt you and cost you what you're carrying, but your land, your buildings and your crafted gear stay yours.</p></details>
        </div>
      </div>
    </section>

    <section class="final" aria-labelledby="cta-h">
      <div class="wrap">
        <img class="mark px" src="${A}/logo-wordmark.png" alt="" aria-hidden="true" width="900" height="175" />
        <h2 id="cta-h">There's a patch of ash with your name on it</h2>
        <p class="lead">Free to play, nothing to install, and the first harvest is two minutes away.</p>
        <div class="cta-row">
          <a class="btn-board btn-board-lg" data-play="" href="#">Play Terraboma</a>
        </div>
      </div>
    </section>
  </main>

  <footer id="site-footer"></footer>`;
}

export function renderLanding(
  container: HTMLElement,
  navigate: (path: string) => void,
): () => void {
  loadCSS();
  // Release the shell's `height: 100%` for this route only. Without it body, not
  // the document, is the scroll container - and every footer/nav anchor updated
  // the hash without scrolling anywhere. See the data-landing block in app.css.
  document.documentElement.setAttribute('data-landing', 'on');
  container.innerHTML = landingBody();
  wirePlayLinks(container, navigate);
  renderFooter(container, 'home');
  initHomeTeasers(container);
  initFaq(container);
  initRails(container);
  // Music, off until the visitor asks for it — see audio/landing-audio.ts.
  const stopAudio = mountLandingAudio(container);

  return () => {
    stopAudio();
    unloadCSS();
    document.documentElement.removeAttribute('data-landing');
    container.innerHTML = '';
  };
}
